import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import { toast } from "react-toastify";

// 🔹 UPDATE MY PROFILE
export const updateMyProfile = createAsyncThunk(
  "auth/updateMyProfile",
  async (formData, thunkAPI) => {
    try {
      const { data } = await api.put("/api/admin/me", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.success) {
        toast.success("Profile updated successfully");
        return data.data;
      }
      return thunkAPI.rejectWithValue(data.message);
    } catch (err) {
      const message = err.response?.data?.message || "Update failed";
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 🔹 Load from storage
const getStoredAuth = () => {
  const token =
    sessionStorage.getItem("adminToken") ||
    localStorage.getItem("adminToken");

  const user =
    sessionStorage.getItem("adminInfo") ||
    localStorage.getItem("adminInfo");

  return {
    token: token || null,
    user: user ? JSON.parse(user) : null,
  };
};

// 🔹 LOGIN (step 1 — credentials)
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, thunkAPI) => {
    try {
      const { data } = await api.post("/api/admin/login", credentials);

      // OTP required — return tempToken, no full JWT yet
      if (data.success && data.otpRequired && data.tempToken) {
        return { otpRequired: true, tempToken: data.tempToken };
      }

      if (data.success && data.token && data.admin) {
        sessionStorage.setItem("adminToken", data.token);
        sessionStorage.setItem("adminInfo", JSON.stringify(data.admin));
        toast.success("Login successful");
        return { otpRequired: false, token: data.token, user: data.admin };
      }

      toast.error(data.message || "Invalid credentials");
      return thunkAPI.rejectWithValue(data.message);
    } catch (err) {
      const message = err.response?.data?.message || "Login failed";
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 🔹 VERIFY OTP (step 2)
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ tempToken, otp }, thunkAPI) => {
    try {
      const { data } = await api.post("/api/admin/verify-otp", { tempToken, otp });
      if (data.success && data.token && data.admin) {
        sessionStorage.setItem("adminToken", data.token);
        sessionStorage.setItem("adminInfo", JSON.stringify(data.admin));
        toast.success("Login successful");
        return { token: data.token, user: data.admin };
      }
      return thunkAPI.rejectWithValue(data.message || "OTP verification failed");
    } catch (err) {
      const message = err.response?.data?.message || "OTP verification failed";
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const stored = getStoredAuth();

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user:        stored.user,
    token:       stored.token,
    loading:     false,
    error:       null,
    otpRequired: false,
    tempToken:   null,
  },

  reducers: {
    // 🔹 LOGOUT
    logout: (state) => {
      sessionStorage.clear();
      localStorage.clear();
      state.user        = null;
      state.token       = null;
      state.error       = null;
      state.otpRequired = false;
      state.tempToken   = null;
      toast.info("Logged out");
    },

    // 🔹 CANCEL OTP step — go back to credentials
    cancelOtp: (state) => {
      state.otpRequired = false;
      state.tempToken   = null;
      state.error       = null;
      state.loading     = false;
    },

    // 🔹 SET USER (after profile update)
    setUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
      const stored = sessionStorage.getItem("adminInfo")
        ? "session"
        : localStorage.getItem("adminInfo")
        ? "local"
        : "session";
      const updatedUser = { ...state.user };
      if (stored === "local") localStorage.setItem("adminInfo", JSON.stringify(updatedUser));
      else sessionStorage.setItem("adminInfo", JSON.stringify(updatedUser));
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.otpRequired) {
          state.otpRequired = true;
          state.tempToken   = action.payload.tempToken;
        } else {
          state.user        = action.payload.user;
          state.token       = action.payload.token;
          state.otpRequired = false;
          state.tempToken   = null;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload;
      })
      .addCase(verifyOtp.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading     = false;
        state.user        = action.payload.user;
        state.token       = action.payload.token;
        state.otpRequired = false;
        state.tempToken   = null;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload;
      })
      .addCase(updateMyProfile.fulfilled, (state, action) => {
        // Merge updated fields — keep token/role intact
        const updated = {
          ...state.user,
          name:         action.payload.name,
          profilePhoto: action.payload.profilePhoto,
          phone:        action.payload.phone,
          department:   action.payload.department,
          designation:  action.payload.designation,
          bio:          action.payload.bio,
          linkedinURL:  action.payload.linkedinURL,
        };
        state.user = updated;
        // Sync to storage
        if (sessionStorage.getItem("adminInfo")) sessionStorage.setItem("adminInfo", JSON.stringify(updated));
        else if (localStorage.getItem("adminInfo")) localStorage.setItem("adminInfo", JSON.stringify(updated));
      });
  },
});

export const { logout, setUser, cancelOtp } = authSlice.actions;
export default authSlice.reducer;